import {Navigate, useLocation} from "react-router-dom";
import React, {FormEvent, ReactElement, useEffect} from "react";
import {authActions, authSelectors} from "./authSlice";
import {loginActions, loginSelectors} from "./loginSlice";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {FormGroup} from "components/UIComponents/FormGroup";
import {FormattedMessage} from "react-intl";
import {isEmpty} from "ramda";
import {ErrorMessage} from "../../components/UIComponents/ErrorMessage";
import {CoverMoodForm} from "../../components/UIComponents/cover-mood-form/CoverMoodForm";
import {PageTitle} from "../../components/UIComponents/PageTitle";
import {FormInput} from "../../components/UIComponents/FormInput";
import {PrimaryButtonBig} from "../../components/UIComponents/PrimaryButtonBig";

export interface LoginPageProps {

}

export function LoginPage(props: LoginPageProps): ReactElement {
    const dispatch = useAppDispatch();
    const loggedIn = useAppSelector(authSelectors.loggedIn);
    const loginError = useAppSelector(authSelectors.loginError);
    const loading = useAppSelector(authSelectors.requestPending);

    const to = useAppSelector(loginSelectors.to);
    const userName = useAppSelector(loginSelectors.userName);
    const userNameError = useAppSelector(loginSelectors.userNameError);
    const password = useAppSelector(loginSelectors.password);
    const passwordError = useAppSelector(loginSelectors.passwordError);
    const setUserName = (userName: string) => {
        dispatch(loginActions.setUserNameError(null));
        dispatch(loginActions.setUserName(userName));
    };
    const setPassword = (password: string) => {
        dispatch(loginActions.setPasswordError(null));
        dispatch(loginActions.setPassword(password));
    };
    const location = useLocation();
    

    useEffect(() => {
        const query = new URLSearchParams(location.search);

        if (query.get('token')) {
            dispatch(authActions.tokenLogin({token: query.get('token')}));
            if (query.get('customer')) {
                dispatch(authActions.setCustomer(query.get('customer')));
            }
        }
    }, [dispatch, location]);

    if (loggedIn) {
        return <Navigate to={to}/>
    }

    const login = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const userNameIsEmpty = isEmpty(userName.trim());
        if (userNameIsEmpty) {
            dispatch(loginActions.setUserNameError("login.userName.error.empty"));
        }

        const passwordIsEmpty = isEmpty(password);
        if (passwordIsEmpty) {
            dispatch(loginActions.setPasswordError("login.password.error.empty"));
        }

        if (userNameIsEmpty || passwordIsEmpty) {
            return;
        }

        dispatch(authActions.login({username: userName, password: password}))
            .then(() => dispatch(loginActions.reset()));
    };

    const submitDisabled = loading || isEmpty(userName) || isEmpty(password);

    return (
        <CoverMoodForm>
            <form className="flex flex-col h-full" onSubmit={login}>
                <div className="flex-auto flex flex-col gap-6 justify-center">
                    <PageTitle>Login</PageTitle>

                    {loginError && <ErrorMessage><FormattedMessage id="login.error.couldNotLogin"/></ErrorMessage>}

                    <div>
                        <FormGroup
                            groupTitle={<span className="text-base"><FormattedMessage id="login.userName"/></span>}
                            error={userNameError ? <FormattedMessage id={userNameError}/> : null}
                        >
                            <FormInput
                                autoFocus
                                value={userName}
                                onChange={e => setUserName(e.target.value)}
                                hasError={Boolean(userNameError)}
                            />
                        </FormGroup>
                    </div>

                    <div>
                        <FormGroup
                            groupTitle={<span className="text-base"><FormattedMessage id="login.password"/></span>}
                            error={passwordError ? <FormattedMessage id={passwordError}/> : null}
                        >
                            <FormInput
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                hasError={false}
                            />
                        </FormGroup>
                    </div>
                </div>

                <div className="flex-none">
                    <PrimaryButtonBig
                        type="submit"
                        disabled={submitDisabled}>
                        <FormattedMessage id="login.submit"/>
                    </PrimaryButtonBig>
                </div>
            </form>

        </CoverMoodForm>
    );
}
